import { THEME_COLORS, DAILY_CARE_CATEGORIES } from '../../../constants/horseConstants';
import { countCompletedDailyTasks, countTotalDailyTasks, getTodayDateString } from '../../../utils/horseHelpers';

// Default icon mapping for daily care categories
const DEFAULT_CATEGORY_ICONS = {
  grain: 'https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/87ygslk4_EEF9C99A-3E5C-407A-9ABD-0E2EE5768BFD.png',
  supplements: 'https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/ut8bj0pe_99F8D3C5-F9F5-47D8-B8B4-9F438660FCF7.png',
  hay: 'https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/sjrwkp18_A11200E0-91BB-4CAB-8860-7555293ABF04.png',
  water: 'https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/zqu8lk8y_38104E52-A59C-4972-B0B8-930DDCA9F925.png',
  grooming: 'https://customer-assets.emergentagent.com/job_mustanglife/artifacts/mk9pxe7f_1604FCBE-AA96-40EC-A37E-2F9FE9F13B1F.png'
};

/**
 * Horse Daily Care Card Component
 * Displays and manages daily care tasks with icons
 * @param {object} mustang - The mustang data
 * @param {function} onCheckboxChange - Callback for checkbox changes
 * @param {array} categories - Custom daily care categories (optional, falls back to defaults)
 */
const HorseDailyCareCard = ({ mustang, onCheckboxChange, categories }) => {
  // Use custom categories if provided, otherwise fall back to constants
  const dailyCareCategories = categories && categories.length > 0 ? categories : DAILY_CARE_CATEGORIES;
  
  // Calculate totals based on the actual categories being used
  const countCompleted = () => {
    let count = 0;
    dailyCareCategories.forEach(category => {
      const categoryData = mustang.daily_care_checklist?.[category.key] || {};
      category.items.forEach(item => {
        if (categoryData[item.key]) count++;
      });
    });
    return count;
  };
  
  const countTotal = () => {
    let count = 0;
    dailyCareCategories.forEach(category => {
      count += category.items.length;
    });
    return count;
  };
  
  const completedCount = countCompleted();
  const totalCount = countTotal();
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const today = getTodayDateString();
  const isToday = mustang.daily_care_date === today;

  // Get icon for a category - use custom icon_url if available, otherwise fall back to defaults
  const getCategoryIcon = (category) => {
    if (category.icon_url) {
      // Handle both relative and absolute URLs
      if (category.icon_url.startsWith('/api/')) {
        return `${process.env.REACT_APP_BACKEND_URL}${category.icon_url}`;
      }
      return category.icon_url;
    }
    return DEFAULT_CATEGORY_ICONS[category.key] || null;
  };

  return (
    <div className="space-y-2">
      {/* Progress Summary - Compact */}
      <div className="bg-gray-50 p-2 rounded-lg">
        <div className="text-xs font-medium mb-1" style={{ color: THEME_COLORS.primary }}>
          Daily Care Progress
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: THEME_COLORS.secondary
                }}
              />
            </div>
          </div>
          <div className="text-sm font-bold" style={{ color: THEME_COLORS.secondary }}>
            {completedCount}/{totalCount}
          </div>
        </div>
        {!isToday && completedCount > 0 && (
          <div className="text-xs text-amber-600 mt-1">
            ⚠️ Tasks from previous day will reset
          </div>
        )}
      </div>

      {/* Daily Care Tasks - Compact */}
      <div className="space-y-2">
        {dailyCareCategories.map((category) => {
          const categoryData = mustang.daily_care_checklist?.[category.key] || {};
          const iconUrl = getCategoryIcon(category);
          
          return (
            <div key={category.key} className="border rounded-lg p-2" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-center gap-1.5 mb-1">
                {iconUrl && (
                  <img 
                    src={iconUrl} 
                    alt={category.label} 
                    className="w-4 h-4 object-contain" 
                  />
                )}
                <span className="text-xs font-medium" style={{ color: THEME_COLORS.secondary }}>
                  {category.label}
                </span>
              </div>
              <div className="ml-5 space-y-0.5">
                {category.items.map((item) => (
                  <label
                    key={item.key}
                    className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={categoryData[item.key] || false}
                      onChange={(e) => onCheckboxChange(mustang, category.key, item.key, e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                      style={{ accentColor: THEME_COLORS.secondary }}
                    />
                    <span
                      className="text-xs"
                      style={{ color: categoryData[item.key] ? THEME_COLORS.secondary : '#6B7280' }}
                    >
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HorseDailyCareCard;
